import { FaroSwitch } from "@components/common/faro-switch";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { SendFeedbackDialog } from "@components/header/send-feedback-dialog";
import { SidebarHolobuilderTooltip } from "@components/sidebar/sidebar-holobuilder/sidebar-holobuilder-tooltip";
import { getHBDashboardUrl } from "@components/sidebar/sidebar-holobuilder/sidebar-holobuilder-utils";
import { FontWeights, neutral } from "@flat-ui";
import { Box, List, Stack, tooltipClasses } from "@mui/material";
import { canAccessHBDashboardSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { withEllipsis } from "@styles/common-styles";
import { SidebarEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

interface Props {
  /** Flag whether the sidebar should be shown full size or minified */
  isSidebarFullSize: boolean;
}

/** Renders a sidebar item that navigates to the HoloBuilder dashboard */
export function SidebarHolobuilderItem({
  isSidebarFullSize,
}: Props): JSX.Element | null {
  const { trackAsyncEvent } = useTrackEvent();

  const [isSwitchChecked, setIsSwitchChecked] = useState<boolean>(true);
  const [shouldShowFeedbackDialog, setShouldShowFeedbackDialog] =
    useState<boolean>(false);
  const isOpenInHBButtonEnabled = useAppSelector(canAccessHBDashboardSelector);

  async function navigateToHBDashboard(): Promise<void> {
    await trackAsyncEvent({
      name: SidebarEvents.navigateToHBDashboard,
    });

    const hbUrl = getHBDashboardUrl();
    window.location.assign(hbUrl);
  }

  function handleFeedbackDialogClose(): void {
    setIsSwitchChecked(true);
    setShouldShowFeedbackDialog(false);
  }

  async function handleSwitchChange(
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> {
    const willBeChecked = event.target.checked;

    await trackAsyncEvent({
      name: SidebarEvents.toggleSDBAccess,
      props: {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Amplitude event property
        UseNewDashboard: willBeChecked,
      },
    });

    if (!willBeChecked) {
      setShouldShowFeedbackDialog(true);
    }

    setIsSwitchChecked(willBeChecked);
  }

  if (!isOpenInHBButtonEnabled) {
    return null;
  }

  return (
    <List
      sx={{
        paddingY: 0,
        mb: "24px",
      }}
    >
      <SphereTooltip
        title={<SidebarHolobuilderTooltip />}
        tooltipProps={{
          sx: {
            [`& .${tooltipClasses.tooltip}`]: {
              maxWidth: 320,
            },
          },
        }}
      >
        <Stack
          direction={"row"}
          spacing={1}
          justifyContent="space-between"
          alignItems={"center"}
          sx={{
            cursor: "pointer",
            pl: "18px",
            pr: "2px",
          }}
        >
          {isSidebarFullSize && (
            <Stack
              sx={{
                ...withEllipsis,
              }}
            >
              <Box
                sx={{
                  color: neutral[800],
                  fontSize: "10px",
                  lineHeight: "14px",
                }}
              >
                Try the
              </Box>
              <Box
                sx={{
                  color: neutral[800],
                  fontSize: "12px",
                  fontWeight: FontWeights.Bold,
                  letterSpacing: "0.48px",
                  textTransform: "uppercase",
                  ...withEllipsis,
                }}
              >
                New Dashboard
              </Box>
            </Stack>
          )}
          <FaroSwitch
            checked={isSwitchChecked}
            onChange={handleSwitchChange}
            sx={{ position: "relative", top: "6px", marginRight: "12px" }}
          />
        </Stack>
      </SphereTooltip>

      <SendFeedbackDialog
        isDialogOpen={shouldShowFeedbackDialog}
        onClose={handleFeedbackDialogClose}
        showXButton={true}
        title="How was your experience with the dashboard?"
        feedbackQuestionText="Before you switch back, can you share some feedback?"
        featureName="Switch to HB Dashboard"
        confirmText="Submit and switch back"
        cancelText="Switch without feedback"
        onSubmitFeedback={navigateToHBDashboard}
        onCancel={navigateToHBDashboard}
      />
    </List>
  );
}
